import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MediaService } from "@/services/media/mediaService";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { UserType } from "@/types/user";

/**
 * Main media hook that provides all media-related queries and mutations
 */
export const useMedia = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // Determine user type for profile picture endpoints
  const userType = user?.type === UserType.Admin ? "admin" : "user";

  // Mutation: Get download URL for a media file
  const getDownloadUrlMutation = useMutation({
    mutationFn: ({
      mediaId,
      expiresIn,
      preferFullSize,
    }: {
      mediaId: string;
      expiresIn?: number;
      preferFullSize?: boolean;
    }) => MediaService.getDownloadUrl(mediaId, expiresIn, preferFullSize),
    onError: (error: any) => {
      toast.error("Kunde inte hämta fil");
    },
  });

  // Query: Get profile picture URL
  const profilePictureQuery = useQuery({
    queryKey: ["profilePicture", userType],
    queryFn: () => MediaService.getProfilePictureUrl(3600, userType),
    enabled: !!user, // Only fetch if user is authenticated
    staleTime: 5 * 60 * 1000, // 5 minutes (presigned URLs expire after 1 hour)
    retry: false, // Don't retry on 404 (no profile picture)
  });

  // Mutation: Delete media
  const deleteMediaMutation = useMutation({
    mutationFn: ({ mediaId, fkType }: { mediaId: string; fkType: string }) =>
      MediaService.deleteMedia(mediaId, fkType),
    onSuccess: (_, variables) => {
      // Invalidate relevant queries based on fkType
      if (variables.fkType === "actorId") {
        queryClient.invalidateQueries({ queryKey: ["profilePicture"] });
        queryClient.invalidateQueries({ queryKey: ["userDetails"] });
        queryClient.invalidateQueries({ queryKey: ["adminProfile"] });
      } else if (variables.fkType === "newsId") {
        queryClient.invalidateQueries({ queryKey: ["news"] });
      } else if (variables.fkType === "campaignId") {
        queryClient.invalidateQueries({ queryKey: ["campaigns"] });
        queryClient.invalidateQueries({ queryKey: ["campaign"] });
      } else if (variables.fkType === "contractId") {
        queryClient.invalidateQueries({ queryKey: ["contracts"] });
        queryClient.invalidateQueries({ queryKey: ["vehicleCard"] });
      } else if (variables.fkType === "invoiceId") {
        queryClient.invalidateQueries({ queryKey: ["invoices"] });
        queryClient.invalidateQueries({ queryKey: ["vehicleCard"] });
      } else if (variables.fkType === "vehicleCardImageId") {
        queryClient.invalidateQueries({ queryKey: ["vehicleCard"] });
      } else if (variables.fkType === "vehicleCardDocumentId") {
        queryClient.invalidateQueries({ queryKey: ["vehicleCard"] });
      }
      toast.success("Fil borttagen");
    },
    onError: (error: any) => {
      toast.error("Kunde inte ta bort fil");
    },
  });

  // Mutation: Delete profile picture
  const deleteProfilePictureMutation = useMutation({
    mutationFn: () => MediaService.deleteProfilePicture(userType),
    onSuccess: () => {
      // Invalidate profile picture query and user details
      queryClient.invalidateQueries({ queryKey: ["profilePicture"] });
      queryClient.invalidateQueries({ queryKey: ["userDetails"] });
      queryClient.invalidateQueries({ queryKey: ["adminProfile"] });
    },
    onError: (error: any) => {},
  });

  return {
    // Profile picture query
    profilePictureUrl: profilePictureQuery.data || null,
    isLoadingProfilePicture: profilePictureQuery.isLoading,
    profilePictureError: profilePictureQuery.error,
    refetchProfilePicture: profilePictureQuery.refetch,

    // Mutations
    getDownloadUrlAsync: getDownloadUrlMutation.mutateAsync,
    deleteMediaAsync: deleteMediaMutation.mutateAsync,
    deleteProfilePicture: deleteProfilePictureMutation.mutateAsync,

    // Mutation states
    isGettingDownloadUrl: getDownloadUrlMutation.isPending,
    isDeletingMedia: deleteMediaMutation.isPending,
    isDeletingProfilePicture: deleteProfilePictureMutation.isPending,

    // Mutation errors
    getDownloadUrlError: getDownloadUrlMutation.error?.message || null,
    deleteMediaError: deleteMediaMutation.error?.message || null,
    deleteProfilePictureError:
      deleteProfilePictureMutation.error?.message || null,
  };
};
